import React from 'react';
import { Snackbar, Alert, Button, useTheme, useMediaQuery } from '@mui/material';
import {
  CheckCircleOutline,
  ErrorOutline,
  WarningAmber,
  InfoOutlined,
} from '@mui/icons-material';
import { useNotification, NotificationSeverity } from '../../contexts/NotificationContext';

const severityIcons = {
  success: CheckCircleOutline,
  error: ErrorOutline,
  warning: WarningAmber,
  info: InfoOutlined,
};

export const NotificationSnackbar: React.FC = () => {
  const { notification, hideNotification } = useNotification();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const IconComponent = notification.severity
    ? severityIcons[notification.severity as NotificationSeverity]
    : InfoOutlined;

  return (
    <Snackbar
      key={notification.key}
      open={notification.open}
      autoHideDuration={notification.duration}
      onClose={hideNotification}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: isMobile ? 'center' : 'right',
      }}
      sx={{
        zIndex: 9999,
        // Responsive positioning
        bottom: { xs: 16, sm: 24 },
        right: { xs: 16, sm: 24 },
        left: isMobile ? 16 : 'auto',
      }}
    >
      <Alert
        onClose={hideNotification}
        severity={notification.severity || 'info'}
        variant="filled"
        icon={<IconComponent />}
        action={
          notification.action ? (
            <Button
              color="inherit"
              size="small"
              onClick={() => {
                notification.action?.onClick();
                hideNotification();
              }}
              sx={{
                fontWeight: 600,
                textTransform: 'none',
              }}
            >
              {notification.action.label}
            </Button>
          ) : undefined
        }
        sx={{
          width: '100%',
          minWidth: isMobile ? '100%' : '320px',
          maxWidth: isMobile ? '100%' : '500px',
          boxShadow: '0 8px 16px rgba(0, 0, 0, 0.2), 0 4px 8px rgba(0, 0, 0, 0.1)',
          borderRadius: 2,
          fontSize: { xs: '0.875rem', sm: '0.9375rem' },
          '& .MuiAlert-icon': {
            fontSize: { xs: 22, sm: 24 },
            alignItems: 'center',
          },
          '& .MuiAlert-message': {
            padding: '8px 0',
            display: 'flex',
            alignItems: 'center',
            wordBreak: 'break-word',
          },
          '& .MuiAlert-action': {
            paddingTop: 0,
            paddingBottom: 0,
            alignItems: 'center',
            marginRight: -1,
          },
          // Enhanced colors for better contrast
          '&.MuiAlert-filledSuccess': {
            backgroundColor: '#10B981',
            color: '#fff',
          },
          '&.MuiAlert-filledError': {
            backgroundColor: '#EF4444',
            color: '#fff',
          },
          '&.MuiAlert-filledWarning': {
            backgroundColor: '#F59E0B',
            color: '#fff',
          },
          '&.MuiAlert-filledInfo': {
            backgroundColor: '#3B82F6',
            color: '#fff',
          },
          // Smooth animation
          animation: 'slideIn 0.3s ease-out',
          '@keyframes slideIn': {
            from: {
              transform: isMobile ? 'translateY(100%)' : 'translateX(100%)',
              opacity: 0,
            },
            to: {
              transform: isMobile ? 'translateY(0)' : 'translateX(0)',
              opacity: 1,
            },
          },
        }}
      >
        {notification.message}
      </Alert>
    </Snackbar>
  );
};
